/**
 * Unit Goals Query Hooks
 *
 * Mirrors useGoalsQuery. Gives UnitGoalsScreen the same instant-from-cache
 * rendering that GoalsScreen already has: React Query shows the last known
 * data synchronously while silently refetching in the background, so
 * tab-switches / back-navigations don't flash a loading spinner.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { unitGoalsApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

interface UnitGoalsListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  unit?: number;
  year?: number;
  approval_status?: string;
}

interface UnitGoalsListResponse {
  results: any[];
  count: number;
}

export function useUnitGoalsQuery(params?: UnitGoalsListParams) {
  return useQuery({
    queryKey: queryKeys.unitGoals.list(params),
    queryFn: async (): Promise<UnitGoalsListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.unit) queryParams.unit = params.unit;
      if (params?.year) queryParams.year = params.year;
      if (params?.approval_status) queryParams.approval_status = params.approval_status;

      return await unitGoalsApi.list(queryParams);
    },
    // Same 5-minute staleTime as goals/initiatives/kpis.
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useUnitGoalDetailQuery(unitGoalId: number) {
  return useQuery({
    queryKey: queryKeys.unitGoals.detail(unitGoalId),
    queryFn: () => unitGoalsApi.get(unitGoalId),
    enabled: !!unitGoalId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useDeleteUnitGoalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (unitGoalId: number) => unitGoalsApi.delete(unitGoalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.all });
    },
  });
}

export function useCreateUnitGoalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => unitGoalsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.all });
    },
  });
}

export function useUpdateUnitGoalMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      unitGoalsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.detail(variables.id) });
    },
  });
}

export default useUnitGoalsQuery;
